import React, { useState } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { Plus, Eye, EyeOff, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

const PaymentForm = () => {
  const { addPayment } = useFinance();
  const [formData, setFormData] = useState({
    name: '',
    category: 'suscripciones',
    amount: '',
    paymentDay: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    installments: '',
    payoffAmount: '',
    canPayOff: false,
    observations: '',
    useExactDates: true
  });
  const [showObservations, setShowObservations] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'useExactDates') {
        if (checked) {
          newData.startMonth = undefined;
          newData.installments = '';
        } else {
          newData.startDate = '';
          newData.endDate = '';
        }
      }

      if (name === 'startDate' && newData.installments && newData.installments > 0) {
        const startDate = new Date(value);
        const monthsToAdd = parseInt(newData.installments);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + monthsToAdd);
        newData.endDate = endDate.toISOString().split('T')[0];
      }

      if (name === 'installments' && newData.startDate && value > 0) {
        const startDate = new Date(newData.startDate);
        const monthsToAdd = parseInt(value);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + monthsToAdd);
        newData.endDate = endDate.toISOString().split('T')[0];
      }

      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('El nombre del pago es obligatorio.');
      return;
    }

    if (formData.useExactDates) {
      if (!formData.startDate) {
        alert('La fecha de inicio es obligatoria cuando usas fechas exactas.');
        return;
      }

      if (formData.endDate && new Date(formData.endDate) < new Date(formData.startDate)) {
        alert('La fecha de finalización no puede ser anterior a la fecha de inicio.');
        return;
      }
    }

    setSubmitting(true);

    const payment = {
      name: formData.name.trim(),
      category: formData.category,
      amount: parseFloat(formData.amount) || 0,
      paymentDay: parseInt(formData.paymentDay) || 0,
      payoffAmount: parseFloat(formData.payoffAmount) || 0,
      canPayOff: formData.canPayOff,
      observations: formData.observations.trim(),
      useExactDates: formData.useExactDates
    };

    if (formData.useExactDates) {
      payment.startDate = formData.startDate;
      payment.endDate = formData.endDate;
      payment.installments = parseInt(formData.installments) || 0;
    } else {
      payment.startMonth = parseInt(formData.startMonth) || new Date().getMonth() + 1;
      payment.installments = parseInt(formData.installments) || 0;
    }

    await addPayment(payment);

    setAddSuccess(true);
    setTimeout(() => setAddSuccess(false), 3000);

    setFormData({
      name: '',
      category: 'suscripciones',
      amount: '',
      paymentDay: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      installments: '',
      payoffAmount: '',
      canPayOff: false,
      observations: '',
      useExactDates: true
    });
    setShowObservations(false);
    setSubmitting(false);
    setShowAdvanced(false);
  };

  const generateDayOptions = () => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(
        <option key={i} value={i}>
          Día {i}
        </option>
      );
    }
    return days;
  };

  const getCategoryColor = (category) => {
    const colors = {
      suscripciones: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      casa: 'bg-blue-100 text-blue-800 border-blue-200',
      plazos: 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[category] || colors.suscripciones;
  };

  return (
    <div className="card mb-6 md:mb-8">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 md:gap-3">
          <h2 className="section-title">
            <span>➕</span>
            <span>Agregar Pago</span>
          </h2>
          <Plus className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
        </div>
        <div className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          )}
        </div>
      </button>

      {/* Collapsible Content */}
      <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[2000px] opacity-100 mt-6' : 'max-h-0 opacity-0'
        }`}>
        <form onSubmit={handleSubmit}>
          {/* Basic Info - Always Visible */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                📝 Nombre del Pago *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ej: Netflix"
                className="input-field"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="category" className="form-label">
                🏷️ Categoría
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="suscripciones">Suscripciones</option>
                <option value="casa">Pagos de Casa</option>
                <option value="plazos">Pagos a Plazo</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
            <div className="form-group">
              <label htmlFor="amount" className="form-label">
                💰 Cantidad (€) *
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="Ej: 15.99"
                step="0.01"
                className="input-field"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentDay" className="form-label">
                📅 Día de Cobro *
              </label>
              <select
                id="paymentDay"
                name="paymentDay"
                value={formData.paymentDay}
                onChange={handleInputChange}
                className="input-field"
                required
              >
                <option value="">Seleccionar día...</option>
                {generateDayOptions()}
              </select>
            </div>
          </div>

          {/* Advanced Options Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors mb-4"
          >
            <span className="text-sm font-medium text-gray-700">
              ⚙️ Opciones avanzadas (fechas, cuotas, observaciones)
            </span>
            {showAdvanced ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {/* Advanced Options */}
          <div className={`overflow-hidden transition-all duration-300 ${showAdvanced ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
            }`}>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  name="useExactDates"
                  checked={formData.useExactDates}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 font-medium">
                  🗓️ Usar fechas exactas
                </span>
              </label>

              {formData.useExactDates ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group mb-0">
                    <label htmlFor="startDate" className="form-label">
                      Fecha de Inicio
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                    />
                  </div>

                  <div className="form-group mb-0">
                    <label htmlFor="endDate" className="form-label">
                      Fecha de Fin (opcional)
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      min={formData.startDate}
                      className="input-field"
                    />
                  </div>
                </div>
              ) : (
                <div className="form-group mb-0">
                  <label htmlFor="startMonth" className="form-label">
                    Mes de Inicio (sistema antiguo)
                  </label>
                  <select
                    id="startMonth"
                    name="startMonth"
                    value={formData.startMonth || new Date().getMonth() + 1}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                    ].map((month, idx) => (
                      <option key={idx + 1} value={idx + 1}>{month}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="form-group mb-0">
                <label htmlFor="installments" className="form-label">
                  📊 Número de Cuotas (0 = fijo)
                </label>
                <input
                  type="number"
                  id="installments"
                  name="installments"
                  value={formData.installments}
                  onChange={handleInputChange}
                  placeholder="Ej: 12"
                  min="0"
                  className="input-field"
                />
              </div>

              <div className="form-group mb-0">
                <label htmlFor="payoffAmount" className="form-label">
                  💳 Pago único (€)
                </label>
                <input
                  type="number"
                  id="payoffAmount"
                  name="payoffAmount"
                  value={formData.payoffAmount}
                  onChange={handleInputChange}
                  placeholder="Ej: 150"
                  step="0.01"
                  className="input-field"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="canPayOff"
                  checked={formData.canPayOff}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Se puede pagar de una vez</span>
              </label>

              <button
                type="button"
                onClick={() => setShowObservations(!showObservations)}
                className="btn btn-sm btn-secondary"
              >
                {showObservations ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showObservations ? 'Ocultar notas' : 'Añadir notas'}
              </button>
            </div>

            {showObservations && (
              <div className="form-group">
                <label htmlFor="observations" className="form-label">
                  📝 Observaciones
                </label>
                <textarea
                  id="observations"
                  name="observations"
                  value={formData.observations}
                  onChange={handleInputChange}
                  placeholder="Notas adicionales..."
                  rows="2"
                  className="input-field resize-none"
                />
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full sm:w-auto"
            >
              <Plus className="w-5 h-5" />
              {submitting ? 'Agregando...' : 'Agregar Pago'}
            </button>

            {addSuccess && (
              <div className="flex items-center justify-center gap-2 text-green-600 animate-fade-in">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">¡Agregado!</span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;